/**
 * Painel de saidas: o que a automacao decidiu, e o override manual.
 *
 * Responsabilidade: listar os interruptores do digital twin (§5) e publicar
 * `override` para o que o usuario apertar.
 *
 * NAO e responsabilidade deste arquivo: montar o comando na mao (isso e o
 * `acoes.comandar`, que passa pela gramatica do servidor) nem inferir o
 * resultado — a tela so muda quando o aparelho publicar o `state` novo. Um
 * botao que se pinta de "ligado" na hora do clique estaria mentindo enquanto o
 * comando ainda esta em transito, e mentindo de vez se o aparelho estiver
 * offline (o broker guarda a mensagem e entrega depois, contrato §3).
 *
 * ## Por que so `override`, e nao `switch`
 *
 * As cinco categorias do §6 existem na API. Aqui aparecem `override` (operar)
 * e, no painel de ajustes, `setpoint`/`photoperiod`/`safety` (conduzir o
 * cultivo). `switch/role` e `switch/forget` sao CONFIGURACAO do aparelho —
 * dizer que tomada e o exaustor, esquecer um plug que sumiu — e isso se faz
 * no painel local, onde quem mexe esta na frente do equipamento e ve a luz
 * acender. Nao e limitacao tecnica: e escolha de superficie, registrada aqui
 * para nao ser lida como esquecimento.
 */

import * as acoes from "./../acoes";
import { iconeDePapel } from "./../icons";
import * as store from "./../store";
import type { Interruptor } from "./../tipos";
import { el, trocar, type Painel } from "./../ui";

const MODOS: { valor: string; rotulo: string }[] = [
  { valor: "on", rotulo: "Ligar" },
  { valor: "off", rotulo: "Desligar" },
  { valor: "auto", rotulo: "Auto" },
];

/** Nomes das funcoes que o `out` da telemetria carrega (contrato §4). */
const FUNCOES_DA_AUTOMACAO = ["luz", "exaustor", "umidificador"] as const;

export function montar(): Painel {
  const automacao = el("div", { class: "automacao" });
  const lista = el("div", { class: "lista-saidas" });
  const raiz = el(
    "section",
    { class: "panel card", id: "saidas" },
    el(
      "div",
      { class: "panel-head" },
      el("span", { class: "panel-title", text: "Saídas" }),
    ),
    automacao,
    lista,
  );

  function atualizar(): void {
    const alvo = store.aparelhoSelecionado();
    const estado = store.obter();
    const ultima =
      alvo === null ? undefined : estado.telemetria[alvo.device.id]?.at(-1);

    trocar(
      automacao,
      ...FUNCOES_DA_AUTOMACAO.map((funcao) => {
        const ligado = ultima?.out?.[funcao] ?? null;
        return el(
          "span",
          { class: `pastilha ${ligado === true ? "ligada" : ""}` },
          el("span", { class: "ponto" }),
          funcao,
        );
      }),
    );

    const interruptores = alvo?.state?.switches ?? [];
    if (interruptores.length === 0) {
      trocar(
        lista,
        el("p", {
          class: "hint",
          text:
            alvo?.state === null || alvo?.state === undefined
              ? "Este aparelho ainda não publicou o estado dele."
              : "Nenhum interruptor registrado neste controlador.",
        }),
      );
      return;
    }

    const podeAgir = alvo !== null;
    trocar(lista, ...interruptores.map((s) => linha(s, alvo?.device.id ?? "", podeAgir)));
  }

  return { raiz, atualizar };
}

function linha(s: Interruptor, deviceId: string, podeAgir: boolean): HTMLElement {
  const chave = `${s.device}/${s.entity}`;
  const nome = s.apelido !== undefined && s.apelido !== "" ? s.apelido : chave;
  const modo = s.mode ?? "auto";

  const botoes = MODOS.map(({ valor, rotulo }) => {
    const ativo =
      (valor === "auto" && modo === "auto") ||
      (valor === "on" && modo === "manual_on") ||
      (valor === "off" && modo === "manual_off");
    return el("button", {
      type: "button",
      class: `chip ${ativo ? "active" : ""} ${ativo && valor === "off" ? "mode-off" : ""}`,
      text: rotulo,
      disabled: !podeAgir,
      onclick: () => {
        void acoes.comandar(deviceId, `${rotulo.toLowerCase()} ${nome}`, {
          cmd: "override",
          key: chave,
          // Sem timeout: `on`/`off` seco fica ate alguem devolver para `auto`.
          // O `:{segundos}` que o firmware aceita (§6, corrigido na C6) nao tem
          // controle aqui de proposito — um campo de duracao sem rotulo de
          // unidade e exatamente como se erra em dez segundos achando que sao
          // dez minutos.
          value: valor,
        });
      },
    });
  });

  return el(
    "div",
    { class: `saida ${s.online === false ? "offline" : ""}` },
    el(
      "div",
      { class: "saida-info" },
      el(
        "div",
        { class: "saida-nome" },
        el("span", { class: `ponto ${s.on === true ? "ligada" : ""}` }),
        iconeDePapel(s.role),
        nome,
      ),
      el("div", { class: "saida-meta" }, s.role !== "none" ? s.role : chave, s.online === false ? " · sem contato" : ""),
    ),
    el("div", { class: "chips" }, ...botoes),
  );
}
